import type { BreadcrumbData } from "@ui/atoms/Breadcrumbs/Breadcrumbs";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";

/**
 * The right-hand side of the app's top bar, offered to whatever page is
 * rendered beneath it.
 *
 * The bar itself is shell (`@layout/AppTopBar`) and `Header` is design system,
 * so neither may import the other's internals; this slot is the contract
 * between them, and it lives here — at the bottom of the stack — because that
 * is the only place both can read it. The shell mounts the slot element and
 * registers it; the `Header` that claims it portals its action cluster into it
 * and drops its own breadcrumbs, which the bar carries.
 *
 * Absent a provider (a story, a test, a page outside the shell) `slot` is null
 * and `Header` renders inline exactly as it always has.
 *
 * One owner at a time. A page can hold several Headers — its own at the top,
 * then one fronting each tab section or table — and only the first to mount
 * takes the bar; the rest keep their controls beside their content. Mount
 * order follows tree order, and the page's header is the first thing on the
 * page, so "first" is the right one without any page having to say so.
 *
 * Ownership is re-arbitrated when it changes hands rather than settled once at
 * mount: releasing notifies every subscribed `Header`, so if the owner
 * unmounts mid-session the next one still mounted takes the bar instead of
 * leaving it empty.
 */
type TopBarSlotValue = {
  slot: HTMLElement | null;
  setSlot: (element: HTMLElement | null) => void;
  /** True if `id` now owns the slot (it was free, or already this id's). */
  claim: (id: string) => boolean;
  release: (id: string) => void;
  /** The id holding the slot, or null while it is free. */
  getOwner: () => string | null;
  subscribe: (listener: () => void) => () => void;
  trail: BreadcrumbData[] | null;
  claimTrail: (id: string, crumbs: BreadcrumbData[]) => void;
  releaseTrail: (id: string) => void;
  getTrailOwner: () => string | null;
};

const TopBarSlotContext = createContext<TopBarSlotValue>({
  claim: () => false,
  claimTrail: () => undefined,
  getOwner: () => null,
  getTrailOwner: () => null,
  release: () => undefined,
  releaseTrail: () => undefined,
  setSlot: () => undefined,
  slot: null,
  subscribe: () => () => undefined,
  trail: null
});

export const TopBarSlotProvider = ({ children }: { children: ReactNode }) => {
  // `setSlot` IS the state setter: React guarantees its identity, so wrapping
  // it in a `useCallback` bought nothing and only hid that it is already stable
  // enough to be passed straight to a callback `ref`.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const ownerRef = useRef<string | null>(null);
  const listenersRef = useRef<Set<() => void>>(new Set());
  const [trail, setTrail] = useState<BreadcrumbData[] | null>(null);
  const trailOwnerRef = useRef<string | null>(null);

  const subscribe = useCallback((listener: () => void) => {
    const listeners = listenersRef.current;
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  const getOwner = useCallback(() => ownerRef.current, []);
  const getTrailOwner = useCallback(() => trailOwnerRef.current, []);
  const notify = useCallback(() => {
    listenersRef.current.forEach((listener) => listener());
  }, []);
  const claim = useCallback(
    (id: string) => {
      if (ownerRef.current === id) return true;
      if (ownerRef.current !== null) return false;
      ownerRef.current = id;
      notify();
      return true;
    },
    [notify]
  );
  const release = useCallback(
    (id: string) => {
      if (ownerRef.current !== id) return;
      ownerRef.current = null;
      notify();
    },
    [notify]
  );
  const claimTrail = useCallback(
    (id: string, crumbs: BreadcrumbData[]) => {
      if (trailOwnerRef.current !== null && trailOwnerRef.current !== id)
        return;
      if (trailOwnerRef.current !== id) {
        trailOwnerRef.current = id;
        notify();
      }
      setTrail(crumbs);
    },
    [notify]
  );
  const releaseTrail = useCallback(
    (id: string) => {
      if (trailOwnerRef.current !== id) return;
      trailOwnerRef.current = null;
      setTrail(null);
      notify();
    },
    [notify]
  );
  const value = useMemo(
    () => ({
      claim,
      claimTrail,
      getOwner,
      getTrailOwner,
      release,
      releaseTrail,
      setSlot,
      slot,
      subscribe,
      trail
    }),
    [
      claim,
      claimTrail,
      getOwner,
      getTrailOwner,
      release,
      releaseTrail,
      setSlot,
      slot,
      subscribe,
      trail
    ]
  );
  return (
    <TopBarSlotContext.Provider value={value}>
      {children}
    </TopBarSlotContext.Provider>
  );
};

export const useTopBarSlot = () => useContext(TopBarSlotContext);
